import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, CheckCircle, Share } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <div className="container mx-auto px-6 pt-32 pb-20">
        <div className="max-w-lg mx-auto text-center space-y-8">
          <Smartphone className="h-16 w-16 text-krecao-yellow mx-auto" />
          <h1 className="text-3xl font-bold">Instale o K-recão Lanches</h1>
          <p className="text-gray-400">
            Tenha acesso rápido ao nosso cardápio direto da tela inicial do seu celular, como um app nativo!
          </p>

          {isInstalled ? (
            <div className="flex flex-col items-center gap-4 p-6 rounded-xl bg-green-900/30 border border-green-700">
              <CheckCircle className="h-10 w-10 text-green-400" />
              <p className="text-green-300 font-semibold">App já instalado! 🎉</p>
            </div>
          ) : isIOS ? (
            <div className="space-y-4 p-6 rounded-xl bg-gray-900 border border-gray-800">
              <Share className="h-8 w-8 text-krecao-yellow mx-auto" />
              <p className="font-semibold text-lg">No iPhone/iPad:</p>
              <ol className="text-left text-gray-300 space-y-2 list-decimal list-inside">
                <li>Toque no botão <strong>Compartilhar</strong> (ícone de quadrado com seta) no Safari</li>
                <li>Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong></li>
                <li>Toque em <strong>"Adicionar"</strong></li>
              </ol>
            </div>
          ) : deferredPrompt ? (
            <Button
              onClick={handleInstall}
              size="lg"
              className="bg-krecao-yellow text-black hover:bg-krecao-yellow/90 text-lg px-8 py-6"
            >
              <Download className="mr-2 h-5 w-5" />
              Instalar Agora
            </Button>
          ) : (
            <div className="space-y-4 p-6 rounded-xl bg-gray-900 border border-gray-800">
              <p className="text-gray-300">
                Abra este site no navegador do seu celular (Chrome ou Safari) para instalar o app.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Install;
